/**
 * How much of a row something occupies, mirroring App\Enums\LayoutWidth.
 *
 * One vocabulary for both levels: a section takes a share of the page's row, a
 * field takes a share of its section's row. Twelve columns is the smallest grid
 * that divides cleanly by both three and four, so sixths, quarters and thirds
 * are exact spans rather than rounded percentages and a row always tiles — the
 * new contact form's identity row is 1/6 + 1/6 + 1/6 + 1/4 + 1/4, or 2+2+2+3+3.
 *
 * `spanMd` is what a width falls back to once the surface gets narrow: anything
 * below a half grows to a half, anything above one takes the whole row. The
 * thresholds that trigger it live in CSS and differ per level, because a section
 * is measured against the page and a field against its section.
 */
export const GRID_COLUMNS = 12;

const SPANS = {
    '1/6': 2,
    '1/4': 3,
    '1/3': 4,
    '1/2': 6,
    '2/3': 8,
    '3/4': 9,
    full: GRID_COLUMNS
};

/** Every step, sixth included — three fields sharing half a row need it. */
export const FIELD_WIDTHS = Object.keys(SPANS);

/** A panel narrower than a quarter of the page holds nothing readable. */
export const SECTION_WIDTHS = FIELD_WIDTHS.filter(w => w !== '1/6');

export function widthSpan(width) {
    return SPANS[width] ?? GRID_COLUMNS;
}

/**
 * Custom properties read by any `.layout-sections-grid` / `.bf-grid` child.
 * Bind on the element that is the direct grid child.
 */
export function widthStyle(width) {
    const span = widthSpan(width);

    return {
        '--layout-span': span,
        '--layout-span-md': span <= 6 ? 6 : GRID_COLUMNS
    };
}

export function sectionWidthStyle(section) {
    return widthStyle(section?.width);
}

/**
 * Which row each card lands on, packing spans into rows of twelve.
 *
 * The same walk the grid does: a card that does not fit in what is left of the
 * current row starts the next one.
 *
 * @param {number[]} spans
 * @returns {number[][]} the card indexes making up each row
 */
function packRows(spans) {
    const rows = [];
    let row = [];
    let used = 0;

    spans.forEach((span, index) => {
        if (row.length && used + span > GRID_COLUMNS) {
            rows.push(row);
            row = [];
            used = 0;
        }

        row.push(index);
        used += span;
    });

    if (row.length) {
        rows.push(row);
    }

    return rows;
}

/** A card with a row to itself takes the whole of it. */
function widenLoneCards(spans) {
    const packed = [...spans];

    for (const row of packRows(spans)) {
        if (row.length === 1) {
            packed[row[0]] = GRID_COLUMNS;
        }
    }

    return packed;
}

/**
 * The widths a tab's cards actually get, in order.
 *
 * A card is given a share of the row, not a promise that something will sit
 * beside it — and what does sit beside it is decided at runtime: a fiche shows
 * the role cards the contact has earned, so the same layout packs differently
 * for a client and for a plain contact. A half-width card that ends up alone
 * leaves half a row of nothing, which reads as a card that failed to load.
 *
 * So the packing is walked here and a card alone on its row is widened to the
 * whole of it. Twice, because the grid changes spans as it narrows: once on the
 * declared widths, once on what they become at the middle step — otherwise a
 * card that is alone only at that width would keep the hole.
 *
 * Only *alone* — a row of two quarters keeps its proportions. Half a row of air
 * beside a single card is a layout accident; the space between two cards the
 * administrator put side by side is a choice.
 *
 * @param {Array<{width?: string}>} sections in the order they are rendered
 * @returns {Array<Record<string, number>>} a style object per card
 */
export function sectionGridStyles(sections) {
    const declared = (sections || []).map(section => widthSpan(section?.width));
    const md = declared.map(span => (span <= 6 ? 6 : GRID_COLUMNS));

    const packed = widenLoneCards(declared);
    const packedMd = widenLoneCards(md);

    return declared.map((_, index) => ({
        '--layout-span': packed[index],
        '--layout-span-md': packedMd[index]
    }));
}

/**
 * A field gives up its width in steps, not all at once.
 *
 * A section that stops fitting becomes a half and then the whole row — it is a
 * panel, and half a narrow page holds nothing. A field is not: it lives in a
 * column that is already a fraction of a card, so a single step would throw
 * away room it still had:
 *
 *   md — nothing narrower than a half
 *   sm — the whole row.
 *
 * The floor used to be a third, which meant a third had no md step at all: a
 * 430px card kept three fields of 129px each, the width where "Date
 * d'activation prospect" wraps over three lines and a select shows four
 * characters. A field's own width is what makes it readable — the label above
 * it needs the same room the control does.
 */
const FIELD_MIN_SPAN_MD = 6;
const FIELD_MIN_SPAN_SM = GRID_COLUMNS;

function fieldSpanStyle(span) {
    const bounded = Math.min(Math.max(span, 1), GRID_COLUMNS);

    return {
        '--layout-span': bounded,
        '--layout-span-md': Math.max(bounded, FIELD_MIN_SPAN_MD),
        '--layout-span-sm': Math.max(bounded, FIELD_MIN_SPAN_SM)
    };
}

export function fieldWidthStyle(field) {
    return fieldSpanStyle(widthSpan(field?.width));
}

/** Fractions speak for themselves, `full` does not. */
export function widthLabel(width) {
    return width === 'full' ? i18n.t('Full width') : width;
}

/**
 * The width matching a span on the retired `columns` / `col_span` pair. Work
 * orders still store their layout that way in the type's JSON config; this is
 * the adapter at their boundary, and it goes when they move over too.
 */
export function widthFromLegacySpan(colSpan, columns) {
    const cols = columns || 2;
    const span = colSpan === 'full' ? cols : Math.min(colSpan || 1, cols);
    const twelfths = Math.round((span / cols) * GRID_COLUMNS);

    return FIELD_WIDTHS.find(w => SPANS[w] >= twelfths) ?? 'full';
}
